import { useState } from "react";
import { CounterpartyDetails } from "@common/schemas/counterpartyDetails.schema";
import { CounterpartyAddress } from "@common/schemas/counterpartyAddress.schema";
import { CounterpartyContact } from "@common/schemas/counterpartyContact.schema";
import {
    useUpdateCounterpartyAddressMutation,
    useUpdateCounterpartyContactMutation,
} from "@api/counterparty.api";
import {
    matchClonedAddress,
    matchClonedContact,
} from "./matchClonedCounterpartyChild";
import {
    AddressReplacement,
    ContactReplacement,
    displayedRemovedIds,
    persistCounterpartyReplacements,
    placeCounterpartyReplacementRows,
} from "./persistCounterpartyReplacements";

interface SourceBooks {
    counterpartyId: string;
    contacts: CounterpartyContact[];
    addresses: CounterpartyAddress[];
}

export function useCounterpartyReplacementBooks({
    counterpartyId,
    details,
    contactRows,
    addressRows,
    contactRowReplacesIds,
    addressRowReplacesIds,
    contactReplacements,
    addressReplacements,
    removedContactIds,
    removedAddressIds,
    fetchDetails,
}: {
    counterpartyId: string;
    details: CounterpartyDetails | undefined;
    contactRows: CounterpartyContact[];
    addressRows: CounterpartyAddress[];
    contactRowReplacesIds: ReadonlyArray<string | null>;
    addressRowReplacesIds: ReadonlyArray<string | null>;
    contactReplacements: ContactReplacement[];
    addressReplacements: AddressReplacement[];
    removedContactIds: readonly string[];
    removedAddressIds: readonly string[];
    fetchDetails: (id: string) => Promise<CounterpartyDetails>;
}) {
    const updateContact = useUpdateCounterpartyContactMutation();
    const updateAddress = useUpdateCounterpartyAddressMutation();
    const [sourceBooks, setSourceBooks] = useState<SourceBooks | null>(null);
    if (details && sourceBooks === null) {
        setSourceBooks({
            counterpartyId,
            contacts: details.contacts,
            addresses: details.addresses,
        });
    }

    const savedContacts = details?.contacts ?? [];
    const savedAddresses = details?.addresses ?? [];
    const frozenBooks =
        sourceBooks ??
        (details
            ? {
                  counterpartyId,
                  contacts: details.contacts,
                  addresses: details.addresses,
              }
            : null);
    const sourceContacts = frozenBooks?.contacts ?? savedContacts;
    const sourceAddresses = frozenBooks?.addresses ?? savedAddresses;

    const saveReplacements = (editedCounterpartyId: string) =>
        persistCounterpartyReplacements({
            counterpartyId: editedCounterpartyId,
            sourceCounterpartyId: frozenBooks?.counterpartyId ?? counterpartyId,
            contacts: frozenBooks?.contacts ?? [],
            addresses: frozenBooks?.addresses ?? [],
            contactReplacements,
            addressReplacements,
            fetchDetails,
            updateContact: updateContact.mutateAsync,
            updateAddress: updateAddress.mutateAsync,
        });

    return {
        contacts: placeCounterpartyReplacementRows({
            saved: savedContacts,
            seeds: contactRows,
            replacesIds: contactRowReplacesIds,
        }),
        addresses: placeCounterpartyReplacementRows({
            saved: savedAddresses,
            seeds: addressRows,
            replacesIds: addressRowReplacesIds,
        }),
        removedContactIds: displayedRemovedIds({
            rows: savedContacts,
            sourceRows: sourceContacts,
            removedIds: removedContactIds.filter((id) =>
                contactRowReplacesIds.includes(id),
            ),
            isClone: (source, row) =>
                matchClonedContact(source, [row]) !== undefined,
        }),
        removedAddressIds: displayedRemovedIds({
            rows: savedAddresses,
            sourceRows: sourceAddresses,
            removedIds: removedAddressIds.filter((id) =>
                addressRowReplacesIds.includes(id),
            ),
            isClone: (source, row) =>
                matchClonedAddress(source, [row]) !== undefined,
        }),
        saveReplacements,
    };
}
